import type { DocMigration } from "../docMigration";

/**
 * Renames "entity tags" to "labels" throughout the doc schema:
 * - `entityTags` table → `labels`
 * - `ENTITY_TAG` entityType → `LABEL` (in labels table and entityRefs table)
 * - `tags` field on taggable entities → `labels`
 * - `viewFilter.tags` → `viewFilter.labels`
 * - `entityTagId` on ReportableUnitDesignation → `labelId`
 */
const migration: DocMigration = {
  id: "20260317_rename_entity_tags_to_labels",
  description: "Rename entity tags to labels",
  apply(json: any): any {
    // 1. Rename top-level entityTags table to labels
    if (json.entityTags !== undefined) {
      json.labels = json.entityTags;
      delete json.entityTags;

      for (const id of Object.keys(json.labels)) {
        if (json.labels[id].entityType === "ENTITY_TAG") {
          json.labels[id] = { ...json.labels[id], entityType: "LABEL" };
        }
      }
    } else {
      json.labels = {};
    }

    // 2. Rename entityType "ENTITY_TAG" → "LABEL" in entityRefs
    const entityRefs = json.entityRefs ?? {};
    for (const id of Object.keys(entityRefs)) {
      if (entityRefs[id]?.entityType === "ENTITY_TAG") {
        entityRefs[id] = { ...entityRefs[id], entityType: "LABEL" };
      }
    }

    // 3. Rename entityTagId → labelId in reportableUnitDesignations
    const designations = json.reportableUnitDesignations ?? {};
    for (const id of Object.keys(designations)) {
      const designation = designations[id];
      if (designation && designation.entityTagId !== undefined) {
        designation.labelId = designation.entityTagId;
        delete designation.entityTagId;
      }
    }

    // 4. Rename tags → labels on labelable entity tables
    const labelableTables = [
      "annotation",
      "ceilings",
      "propInstances",
      "sheets",
      "contentTemplates",
      "viewTemplateOverrides",
      "componentInstances",
      "rooms",
      "roofs",
      "floorSystems",
      "propAttributes",
    ];

    for (const tableName of labelableTables) {
      const table = json[tableName] ?? {};
      for (const id of Object.keys(table)) {
        if (table[id]?.tags !== undefined) {
          table[id].labels = table[id].tags;
          delete table[id].tags;
        }
      }
    }

    // 5. Rename tags → labels in annotation templates (nested in settings)
    const settings = json.settings ?? {};
    for (const settingsId of Object.keys(settings)) {
      const settingsFragment = settings[settingsId];
      const annotationTemplates = settingsFragment?.annotationTemplates ?? {};
      for (const templateId of Object.keys(annotationTemplates)) {
        const template = annotationTemplates[templateId];
        if (template?.tags !== undefined) {
          template.labels = template.tags;
          delete template.tags;
        }
      }

      // 6. Rename tags → labels in prop types (nested in settings)
      const propTypes = settingsFragment?.propTypes ?? {};
      for (const propId of Object.keys(propTypes)) {
        const prop = propTypes[propId];
        if (prop?.tags !== undefined) {
          prop.labels = prop.tags;
          delete prop.tags;
        }
      }
    }

    // 8. Rename viewFilter.tags → viewFilter.labels on views
    const viewTables = ["planViews", "elevationViews", "sectionViews", "view3Ds"];
    for (const tableName of viewTables) {
      const table = json[tableName] ?? {};
      for (const id of Object.keys(table)) {
        const viewFilter = table[id].viewFilter;
        if (viewFilter?.tags !== undefined) {
          viewFilter.labels = viewFilter.tags;
          delete viewFilter.tags;
        }
      }
    }

    // 9. Rename selectedReportingUnitTagId -> selectedReportingUnitLabelId on view3Ds
    const view3Ds = json.view3Ds ?? {};
    for (const id of Object.keys(view3Ds)) {
      const view3D = view3Ds[id];
      if (view3D?.selectedReportingUnitTagId !== undefined) {
        view3D.selectedReportingUnitLabelId = view3D.selectedReportingUnitTagId;
        delete view3D.selectedReportingUnitTagId;
      }
    }

    return json;
  },
};

export default migration;
